/*
	Kalendarz laureatów w sekcji „Zwycięzcy”.

	Jedno żądanie obsługuje i wybór dnia, i przewijanie miesiąca — endpoint zwraca oba
	fragmenty naraz (patrz App\Controller\WinnersCalendarController), bo zmiana miesiąca
	też przerysowuje zaznaczenie, a dwa osobne żądania potrafiłyby się rozminąć.

	Kalendarz i lista są podmieniane w całości, więc słuchamy zdarzeń delegowanych na
	karcie, a nie na przyciskach.
*/

const SELECTOR = '[data-winners]';

/**
 * Loader pokazujemy dopiero po chwili. Odpowiedź z tego endpointu zwykle wraca w kilkadziesiąt
 * milisekund i mignięcie kropek na taki czas wygląda jak usterka, a nie jak ładowanie.
 */
const LOADER_DELAY = 180;

class WinnersCalendar {
	constructor(root) {
		this.root = root;
		this.url = root.dataset.winnersUrl;
		this.date = root.dataset.winnersDate;
		this.month = null;
		this.calendar = root.querySelector('[data-winners-calendar]');
		this.list = root.querySelector('[data-winners-list]');
		this.listBox = root.querySelector('.winners__list');
		this.dot = root.querySelector('.winners__scrollbar-dot');
		this.scroll = null;
		this.request = null;
		this.loaderTimer = null;

		root.addEventListener('click', (event) => this.onClick(event));
		window.addEventListener('resize', () => this.syncScrollbar());
		this.bindScroll();
	}

	/*
		Wskaźnik przewijania z makiety. Pole przewijane jest wymieniane przy każdym żądaniu,
		więc po podmianie trzeba je znaleźć na nowo i podpiąć nasłuch od zera.
	*/
	bindScroll() {
		this.scroll = this.list.querySelector('.winners__scroll');
		if (this.scroll) {
			this.scroll.addEventListener('scroll', () => this.syncScrollbar(), { passive: true });
		}
		this.syncScrollbar();
	}

	syncScrollbar() {
		if (!this.scroll || !this.dot || !this.listBox) {
			return;
		}

		const hidden = this.scroll.scrollHeight - this.scroll.clientHeight;
		// prowadnica ma tę samą wysokość co pole przewijania, a kropka jeździ po niej
		// w granicach pomniejszonych o własną średnicę
		const travel = this.scroll.clientHeight - this.dot.offsetHeight;

		this.listBox.classList.toggle('is-scrollable', hidden > 1);

		if (hidden > 1 && travel > 0) {
			this.dot.style.setProperty('--dot-y', `${(this.scroll.scrollTop / hidden) * travel}px`);
		}
	}

	onClick(event) {
		const day = event.target.closest('[data-winners-day]');
		if (day) {
			event.preventDefault();
			this.load({ data: day.dataset.winnersDay, miesiac: day.dataset.winnersDay });
			return;
		}

		const month = event.target.closest('[data-winners-month]');
		// pusty atrybut = brzeg zakresu, przycisk jest wtedy `disabled`
		if (month && month.dataset.winnersMonth) {
			event.preventDefault();
			// dzień zostaje ten sam — przewijamy tylko widok, nie zmieniamy wyboru
			this.load({ data: this.date, miesiac: month.dataset.winnersMonth });
		}
	}

	load(params) {
		// przy szybkim klikaniu w strzałki liczy się tylko ostatnie żądanie
		if (this.request) {
			this.request.abort();
		}
		this.request = new AbortController();

		this.showLoader();

		const url = `${this.url}?${new URLSearchParams(params)}`;

		fetch(url, { signal: this.request.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
			.then((response) => {
				if (!response.ok) {
					throw new Error(`HTTP ${response.status}`);
				}
				return response.json();
			})
			.then((data) => {
				this.date = data.date;
				this.month = data.month;
				this.root.dataset.winnersDate = data.date;
				this.calendar.innerHTML = data.calendarHtml;
				this.list.innerHTML = data.listHtml;
				this.bindScroll();
				this.hideLoader();
			})
			.catch((error) => {
				if (error.name === 'AbortError') {
					return;
				}
				// nie zostawiamy sekcji w stanie ładowania — użytkownik widzi poprzedni dzień
				this.hideLoader();
			});
	}

	showLoader() {
		clearTimeout(this.loaderTimer);
		this.loaderTimer = setTimeout(() => this.root.classList.add('is-loading'), LOADER_DELAY);
	}

	hideLoader() {
		clearTimeout(this.loaderTimer);
		this.root.classList.remove('is-loading');
	}
}

document.querySelectorAll(SELECTOR).forEach((root) => new WinnersCalendar(root));
